// Series primitive that draws swing volume profiles on the main pane:
// per-leg horizontal volume bars (width ∝ volume at that price), the Point of
// Control line (busiest price = strongest S/R), a dotted pivot-to-pivot
// connector, and the leg's volume delta. Attached to the candlestick series
// via series.attachPrimitive(); data injected with updateData().

const UP_COLOR = '20, 184, 166';    // teal
const DOWN_COLOR = '249, 115, 22';  // orange

class SwingProfileRenderer {
  constructor(source) {
    this._source = source;
  }

  draw(target) {
    const source = this._source;
    const { _chart: chart, _series: series, _profiles: profiles } = source;
    if (!chart || !series || !profiles || profiles.length === 0) return;

    target.useMediaCoordinateSpace(({ context: ctx }) => {
      const timeScale = chart.timeScale();

      for (const leg of profiles) {
        const x0 = timeScale.timeToCoordinate(leg.startTime);
        const x1 = timeScale.timeToCoordinate(leg.endTime);
        if (x0 === null || x1 === null) continue;
        const halfSpan = Math.max(6, (x1 - x0) / 2);
        const rgb = leg.direction === 'up' ? UP_COLOR : DOWN_COLOR;

        // volume bins as horizontal bars growing from the leg start
        for (const bin of leg.bins) {
          const yLow = series.priceToCoordinate(bin.low);
          const yHigh = series.priceToCoordinate(bin.high);
          if (yLow === null || yHigh === null) continue;
          const alpha = 0.12 + bin.ratio * 0.4;
          ctx.fillStyle = `rgba(${rgb}, ${alpha})`;
          ctx.fillRect(x0, yHigh, bin.ratio * halfSpan, yLow - yHigh);
        }

        // pivot-to-pivot connector (the swing "zigzag")
        const yStart = series.priceToCoordinate(leg.startPrice);
        const yEnd = series.priceToCoordinate(leg.endPrice);
        if (yStart !== null && yEnd !== null) {
          ctx.strokeStyle = `rgba(${rgb}, 0.9)`;
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 3]);
          ctx.beginPath();
          ctx.moveTo(x0, yStart);
          ctx.lineTo(x1, yEnd);
          ctx.stroke();
          ctx.setLineDash([]);
        }

        // volume delta tag near the leg start
        if (yStart !== null) {
          const deltaText = `Δ ${leg.deltaPct >= 0 ? '+' : ''}${leg.deltaPct.toFixed(0)}%`;
          ctx.font = '10px sans-serif';
          ctx.fillStyle = `rgba(${rgb}, 1)`;
          ctx.fillText(deltaText, x0 + 2, yStart + (leg.direction === 'up' ? 12 : -4));
        }
      }
    });
  }
}

class SwingProfilePaneView {
  constructor(source) {
    this._renderer = new SwingProfileRenderer(source);
  }

  zOrder() {
    return 'bottom'; // behind candles
  }

  renderer() {
    return this._renderer;
  }
}

export default class SwingProfilePrimitive {
  constructor() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    this._profiles = [];
    this._paneViews = [new SwingProfilePaneView(this)];
  }

  attached({ chart, series, requestUpdate }) {
    this._chart = chart;
    this._series = series;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  updateData(profiles) {
    this._profiles = profiles || [];
    if (this._requestUpdate) this._requestUpdate();
  }

  paneViews() {
    return this._paneViews;
  }
}
